import React, { useState, useRef, useEffect } from 'react';
import DatePickerTrigger from './components/datepicker/DatePickerTrigger';
import CalendarHeader from './components/datepicker/CalendarHeader';
import CalendarGrid from './components/datepicker/CalendarGrid';
import { formatDate, isValidDate } from './components/datepicker/CalendarUtils';
import positionDropdown from '@utils/dropdownPosition';

/**
 * 프리미엄 커스텀 데이트 픽커 컴포넌트
 * 트리거와 달력 팝업으로 구성되며, 외부 클릭 시 닫히는 기능을 포함합니다.
 */
const CustomDatePicker = ({ value, onChange, placeholder = "연도-월-일", align = "left" }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [viewDate, setViewDate] = useState(() => {
        if (isValidDate(value)) {
            return new Date(value);
        }
        return new Date();
    });
    const dropdownRef = useRef(null);
    const popupRef = useRef(null);

    // 외부 클릭 시 닫기 로직
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (!isOpen) return;
        const rafId = requestAnimationFrame(() => {
            if (dropdownRef.current && popupRef.current) {
                positionDropdown(dropdownRef.current, popupRef.current);
            }
        });
        const handleResize = () => {
            if (dropdownRef.current && popupRef.current) {
                positionDropdown(dropdownRef.current, popupRef.current);
            }
        };
        window.addEventListener('resize', handleResize);
        return () => {
            cancelAnimationFrame(rafId);
            window.removeEventListener('resize', handleResize);
        };
    }, [isOpen]);

    // 월 이동 핸들러
    const handlePrevMonth = (e) => {
        if (e) e.stopPropagation();
        setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
    };

    const handleNextMonth = (e) => {
        if (e) e.stopPropagation();
        setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));
    };

    // 날짜 선택 핸들러
    const handleDateSelect = (year, month, day) => {
        onChange(formatDate(year, month, day));
        setIsOpen(false);
    };

    // 오늘로 이동 핸들러
    const handleGoToToday = (e) => {
        e.stopPropagation();
        const today = new Date();
        setViewDate(today);
        handleDateSelect(today.getFullYear(), today.getMonth(), today.getDate());
    };

    // 드롭다운 위치 스타일 계산
    const getDropdownStyle = () => {
        const baseStyle = {
            width: '250px',
            height: 'auto',
            display: 'flex',
            flexDirection: 'column',
            minWidth: '250px',
            boxSizing: 'border-box'
        };

        if (align === 'right') return { ...baseStyle, right: 0 };
        if (align === 'left') return { ...baseStyle, left: 0 };

        // 수치형 정렬 처리
        const leftValue = align.includes('px') || align.includes('rem') || align.includes('%')
            ? align
            : align.startsWith('-left-')
                ? `-${parseInt(align.split('-left-')[1]) * 4}px`
                : align.startsWith('left-')
                    ? `${parseInt(align.split('left-')[1]) * 4}px`
                    : isNaN(parseInt(align)) ? '0' : `${parseInt(align) * 4}px`;

        return { ...baseStyle, left: leftValue };
    };

    return (
        <div className="relative inline-block" ref={dropdownRef}>
            <DatePickerTrigger
                value={value}
                placeholder={placeholder}
                isOpen={isOpen}
                onClick={() => setIsOpen(!isOpen)}
            />

            {isOpen && (
                <div
                    ref={popupRef}
                    className={`absolute top-full mt-2 z-[100] bg-white border border-gray-100 shadow-[0_20px_50px_rgba(0,0,0,0.15)] rounded-2xl p-2.5 origin-top`}
                    style={getDropdownStyle()}
                >
                    <CalendarHeader
                        viewDate={viewDate}
                        onPrev={handlePrevMonth}
                        onNext={handleNextMonth}
                    />

                    <CalendarGrid
                        viewDate={viewDate}
                        value={value}
                        onDateSelect={handleDateSelect}
                    />

                    {/* 오늘로 이동 버튼 */}
                    <div className="mt-3 pt-2 border-t border-gray-50 flex justify-center" style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                        <button
                            onClick={handleGoToToday}
                            className="text-[10px] font-bold text-blue-500 hover:text-blue-700 transition-colors"
                        >
                            오늘로 이동
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CustomDatePicker;
